import { numberToText } from "@/shared/utils";

const BLANK = "_____________";
const MAX_DAY_HOURS = 10;

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

const WEEK_DAYS_RU: Record<string, string> = {
  Mon: "понедельник",
  Tue: "вторник",
  Wed: "среда",
  Thu: "четверг",
  Fri: "пятница",
  Sat: "суббота",
  Sun: "воскресенье",
};

const WEEK_DAYS_KK: Record<string, string> = {
  Mon: "дүйсенбі",
  Tue: "сейсенбі",
  Wed: "сәрсенбі",
  Thu: "бейсенбі",
  Fri: "жұма",
  Sat: "сенбі",
  Sun: "жексенбі",
};

const WORKING_DAYS_ADJECTIVE_RU: Record<number, string> = {
  1: "однодневной",
  2: "двухдневной",
  3: "трехдневной",
  4: "четырехдневной",
  5: "пятидневной",
  6: "шестидневной",
};

const WORKING_DAYS_ORDINAL_RU: Record<number, string> = {
  1: "одно",
  2: "двух",
  3: "трех",
  4: "четырех",
  5: "пяти",
  6: "шести",
};

const WORKING_DAYS_ORDINAL_KK: Record<number, string> = {
  1: "бір",
  2: "екі",
  3: "үш",
  4: "төрт",
  5: "бес",
  6: "алты",
};

const DAYS_OFF_INSTRUMENTAL_RU: Record<number, string> = {
  1: "одним",
  2: "двумя",
  3: "тремя",
  4: "четырьмя",
  5: "пятью",
  6: "шестью",
};

const BREAK_PHRASE_RU = ", с перерывом на обед с {{break_start_time}} до {{break_end_time}} часов";
const BREAK_PHRASE_KK = ", түскі үзіліс сағ. {{break_start_time}}-ден {{break_end_time}}-ға дейін";

const WORK_SCHEDULE_CLAUSE_RU =
  "Работнику устанавливается режим рабочего времени: продолжительностью {{total_hours_phrase_ru}} в неделю по {{day_hours_phrase_ru}} в день с {{work_start_time}} до {{work_end_time}}" +
  BREAK_PHRASE_RU +
  ", при {{working_days}}-дневной ({{working_days_adjective_ru}}) рабочей неделе с {{daysoff_ru}}.";

const WORK_SCHEDULE_CLAUSE_KK =
  "Жұмыскерге келесі жұмыс уақыты режимі белгіленеді: ұзақтығы аптасына {{total_hours_phrase_kk}} және {{work_start_time}}–{{work_end_time}} аралығында күніне {{day_hours_phrase_kk}} құрайды және {{working_days}} ({{working_days_text_kk}}) күндік жұмыс аптасы" +
  BREAK_PHRASE_KK +
  ", {{daysoff_kk}}.";

export const WORK_SCHEDULE_KEYS = [
  "work_start_time",
  "work_end_time",
  "break_start_time",
  "break_end_time",
  "working_days",
  "working_days_text_ru",
  "working_days_text_kk",
  "working_days_adjective_ru",
  "working_days_count_ordinal_ru",
  "working_days_count_ordinal_kk",
  "working_days_list_ru",
  "working_days_list_kk",
  "day_hours",
  "day_hours_text_ru",
  "day_hours_text_kk",
  "day_hours_phrase_ru",
  "day_hours_phrase_kk",
  "total_hours",
  "total_hours_text_ru",
  "total_hours_text_kk",
  "total_hours_phrase_ru",
  "total_hours_phrase_kk",
  "daysoff_ru",
  "daysoff_kk",
] as const;

export interface WorkScheduleInput {
  work_start_time?: string | null;
  work_end_time?: string | null;
  break_start_time?: string | null;
  break_end_time?: string | null;
  working_days_list?: readonly string[] | null;
  standard_hours_per_day?: number | null;
}

export type WorkScheduleContext = Record<(typeof WORK_SCHEDULE_KEYS)[number], string>;

export function isWorkScheduleKey(key: string): boolean {
  return (WORK_SCHEDULE_KEYS as readonly string[]).includes(key);
}

export function parseTimeToMinutes(timeStr: string): number {
  const [hours, minutes] = timeStr.split(":").map(Number);
  return hours * 60 + minutes;
}

function toHHMM(time: string): string {
  const parts = time.split(":");
  return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : time;
}

export function joinDays(days: readonly string[], lang: "ru" | "kk"): string {
  const names = days.map((d) => (lang === "ru" ? WEEK_DAYS_RU[d] : WEEK_DAYS_KK[d]) || d);
  const conjunction = lang === "ru" ? " и " : " және ";
  return names.length > 1 ? names.slice(0, -1).join(", ") + conjunction + names[names.length - 1] : names[0];
}

export function hoursWordRu(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 14) return "часов";
  if (mod10 === 1) return "час";
  if (mod10 >= 2 && mod10 <= 4) return "часа";
  return "часов";
}

function wordsRu(n: number): string {
  return numberToText(n, "ru").replace(/одна$/, "один").replace(/две$/, "два");
}

function hoursPhraseRu(hours: number, perDay: boolean): string {
  if (perDay && hours === 1) return "1 (одному) часу";
  return `${hours} (${wordsRu(hours)}) ${hoursWordRu(hours)}`;
}

function hoursPhraseKk(hours: number): string {
  return `${hours} (${numberToText(hours, "kk")}) сағатты`;
}

export function buildWorkScheduleContext(data: WorkScheduleInput): WorkScheduleContext | null {
  const { work_start_time, work_end_time, working_days_list } = data;
  if (!work_start_time || !work_end_time || !working_days_list) return null;
  if (working_days_list.length < 1 || working_days_list.length > 6) return null;

  const hasBreak = Boolean(data.break_start_time && data.break_end_time);
  const workMinutes = parseTimeToMinutes(work_end_time) - parseTimeToMinutes(work_start_time);
  const breakMinutes = hasBreak
    ? parseTimeToMinutes(data.break_end_time as string) - parseTimeToMinutes(data.break_start_time as string)
    : 0;
  const netMinutes = workMinutes - breakMinutes;
  if (netMinutes <= 0) return null;

  const derivedHours = Math.floor(netMinutes / 60);
  if (derivedHours < 1 || derivedHours > MAX_DAY_HOURS) return null;

  const dayHours = data.standard_hours_per_day || derivedHours;
  if (dayHours < 1 || dayHours > MAX_DAY_HOURS) return null;

  const workingDays = working_days_list.length;
  const totalHours = dayHours * workingDays;
  const daysOff = WEEK_DAYS.filter((d) => !working_days_list.includes(d));
  const daysOffCount = daysOff.length;

  return {
    work_start_time: toHHMM(work_start_time),
    work_end_time: toHHMM(work_end_time),
    break_start_time: hasBreak ? toHHMM(data.break_start_time as string) : "",
    break_end_time: hasBreak ? toHHMM(data.break_end_time as string) : "",
    working_days: String(workingDays),
    working_days_text_ru: wordsRu(workingDays),
    working_days_text_kk: numberToText(workingDays, "kk"),
    working_days_adjective_ru: WORKING_DAYS_ADJECTIVE_RU[workingDays],
    working_days_count_ordinal_ru: WORKING_DAYS_ORDINAL_RU[workingDays],
    working_days_count_ordinal_kk: WORKING_DAYS_ORDINAL_KK[workingDays],
    working_days_list_ru: working_days_list.map((d) => WEEK_DAYS_RU[d] || d).join(", "),
    working_days_list_kk: working_days_list.map((d) => WEEK_DAYS_KK[d] || d).join(", "),
    day_hours: String(dayHours),
    day_hours_text_ru: wordsRu(dayHours),
    day_hours_text_kk: numberToText(dayHours, "kk"),
    day_hours_phrase_ru: hoursPhraseRu(dayHours, true),
    day_hours_phrase_kk: hoursPhraseKk(dayHours),
    total_hours: String(totalHours),
    total_hours_text_ru: wordsRu(totalHours),
    total_hours_text_kk: numberToText(totalHours, "kk"),
    total_hours_phrase_ru: hoursPhraseRu(totalHours, false),
    total_hours_phrase_kk: hoursPhraseKk(totalHours),
    daysoff_ru: `${daysOffCount} (${DAYS_OFF_INSTRUMENTAL_RU[daysOffCount]}) ${daysOffCount === 1 ? "выходным днем" : "выходными днями"} – ${joinDays(daysOff, "ru")}`,
    daysoff_kk: `${daysOffCount} (${numberToText(daysOffCount, "kk")}) демалыс күні – ${joinDays(daysOff, "kk")}`,
  };
}

export function stripWorkScheduleBreakPhrase(content: string): string {
  return content.replace(BREAK_PHRASE_RU, "").replace(BREAK_PHRASE_KK, "");
}

function fillWorkScheduleTemplate(template: string, context: WorkScheduleContext): string {
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) =>
    isWorkScheduleKey(key) ? context[key as keyof WorkScheduleContext] : match,
  );
}

export function renderWorkScheduleText(data: WorkScheduleInput, lang: "ru" | "kk"): string {
  const context = buildWorkScheduleContext(data);
  if (!context) return BLANK;

  const template = lang === "ru" ? WORK_SCHEDULE_CLAUSE_RU : WORK_SCHEDULE_CLAUSE_KK;
  return fillWorkScheduleTemplate(context.break_start_time ? template : stripWorkScheduleBreakPhrase(template), context);
}
